import React from 'react';
import {
  Box, Typography, Button, TextField, IconButton, Divider,
  FormControl, List, MenuItem, Select,
} from '@mui/material';
import {
  Star as StarIcon, ArrowForward as ArrowForwardIcon,
  Lock as LockIcon, ShoppingCart as ShoppingCartIcon,
} from '@mui/icons-material';
import FAQAccordion from '../../../FAQAccordion';
import ComparisonTable from '../../../ComparisonTable';
import TwoColumnComparisonTable from '../../../TwoColumnComparisonTable';
import FittableImage from '../../../FittableImage';
import CircularImageCropper from '../../../CircularImageCropper';
import ImageCropper from '../../../ImageCropper';
import Checklist from '../../../Checklist';
import CenteredVideoPlayer from '../../../CenteredVideoPlayer';
import ResizableButton from '../../../ResizableButton';
import InlineRowLayout from '../../../InlineRowLayout';
import ExtractedElementControls from '../../../ElementControls';
import { getPreset, getPresetFontFamilies } from '../../../../textStyle.js';
import { getListIcon } from '../../../../utils/offerListIcons';
import { ELEMENT_TYPES, LAYOUT_TYPES } from '../../../../utils/offerElementTypes';
import { LAYOUT_CONFIGS } from '../../../../utils/offerLayoutConfigs';
import { calculateVideoAspectRatio } from '../../../../utils/offerPreviewElement';

export const renderImageElements = (element, ctx) => {
  const {
    isEditing, isEditingField, handleDoubleClick,
    isElementSelected, getSelectionStyles, ElementControls,
    editingElement, editingField, tempValue, setTempValue, textEditorRef,
    selectedElement, selectedElementContext, selectedSubElement,
    iconSelectorOpen, setIconSelectorOpen, isBrandsPage, paginationEnabled,
    currentPageIndex, pageSettings, forceRender, rightPanelOpen,
    setSelectedElement, setSelectedElementContext, setSelectedSubElement,
    setTabValue, setRightPanelOpen, setPages, setElements,
    setInsertAfterElementId, setPromptDialogTitle, setPromptDialogMessage,
    setPromptDialogValue, setPromptAction, setShowPromptDialog,
    startInlineEdit, saveInlineEdit, cancelInlineEdit, handleKeyPress,
    handleResizeStart, findAndUpdateLayout, moveElement, generateId,
    deleteElement, deleteElementFromZone, updateElement, applyHighlight,
    handleDragOver, handleDragLeave, handleDrop,
    getZoneHeightStyle, getHeightModeText, toggleZoneHeight, getHeightTooltip,
    getExpandIcon, selectedVariantKey, setSelectedVariantKey, targetZone,
    isInZone, layoutId, zoneId, elementIndex,
    renderInlineEditableElement,
  } = ctx;

  switch (element.type) {
case ELEMENT_TYPES.IMAGE:
  return (
    <Box
      data-element-id={element.id}
      sx={{
        textAlign: 'center',
        position: 'relative',
        '&:hover .element-controls': { opacity: 1 },
        '&:hover .resize-handles': { opacity: 1 },
        mb: 0
      }}
    >
      <ElementControls />
      {isEditingField('content') ? (
        <TextField
          value={tempValue}
          onChange={(e) => setTempValue(e.target.value)}
          onKeyDown={handleKeyPress}
          onBlur={saveInlineEdit}
          autoFocus
          variant="outlined"
          size="small"
          fullWidth
          placeholder="Enter image URL"
          sx={{ mb: 0 }}
        />
      ) : (
        <Box
          sx={{
            cursor: 'pointer',
            border: '2px dashed transparent',
            '&:hover': {
              opacity: 0.8,
              border: '2px dashed #1976d2'
            },
            p: 1,
            borderRadius: 1,
            position: 'relative',
            display: 'inline-block',
            ...getSelectionStyles(element.id)
          }}
          onClick={(e) => {
            // Don't handle clicks on resize handles
            if (e.target.closest('.resize-handles')) {
              return;
            }
            e.stopPropagation();
            e.preventDefault();
            console.log('🔴 Image clicked - selecting element:', element.id);
            console.log('🔴 Event target:', e.target);
            console.log('🔴 Event currentTarget:', e.currentTarget);
            console.log('🔴 Before selection - selectedElement:', selectedElement?.id);
            console.log('🔴 Before selection - rightPanelOpen:', rightPanelOpen);
            setSelectedElement(element);
            setSelectedElementContext({ isInZone, layoutId, zoneId });
            setTabValue(0); // Set to Content tab for editing image URL
            setRightPanelOpen(true); // Ensure right panel is open
            console.log('🔴 After selection calls made');
          }}
          onDoubleClick={() => handleDoubleClick('content', element.content)}
          title="Click to select and edit styles • Double-click to change URL"
        >
          {element.content ? (
            <Box sx={{ position: 'relative', display: 'inline-block' }}>
              <img
                src={element.content}
                alt={element.alt || 'Image'}
                style={{
                  maxWidth: '100%',
                  height: 'auto',
                  maxHeight: '150px',
                  width: element.styles?.width || 'auto',
                  height: element.styles?.height || 'auto',
                  display: 'block'
                }}
                onLoad={(e) => {
                  console.log('🔴 Image loaded:', {
                    naturalWidth: e.target.naturalWidth,
                    naturalHeight: e.target.naturalHeight,
                    currentWidth: e.target.width,
                    currentHeight: e.target.height
                  });
                }}
              />
              {console.log('🔴 Rendering resize handles for image:', element.id)}

              {/* Resize Handles */}
              <Box
                className="resize-handles"
                sx={{
                  position: 'absolute',
                  top: -6,
                  left: -6,
                  right: -6,
                  bottom: -6,
                  opacity: 1, // Temporarily make visible for debugging
                  transition: 'opacity 0.2s',
                  pointerEvents: 'none',
                  zIndex: 10,
                  border: '2px solid green' // Debug border
                }}
              >
                {/* Corner resize handles */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: -6,
                    left: -6,
                    width: 12,
                    height: 12,
                    backgroundColor: '#ff0000', // Red for debugging
                    border: '2px solid white',
                    borderRadius: '50%',
                    cursor: 'nw-resize',
                    pointerEvents: 'auto',
                    zIndex: 11
                  }}
                  onMouseDown={(e) => {
                    console.log('🔴 Resize handle clicked:', 'nw', e);
                    e.stopPropagation();
                    e.preventDefault();
                    handleResizeStart(e, element, 'nw');
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: -6,
                    right: -6,
                    width: 12,
                    height: 12,
                    backgroundColor: '#1976d2',
                    border: '2px solid white',
                    borderRadius: '50%',
                    cursor: 'ne-resize',
                    pointerEvents: 'auto',
                    zIndex: 11
                  }}
                  onMouseDown={(e) => {
                    console.log('🔴 Resize handle clicked:', 'ne');
                    handleResizeStart(e, element, 'ne');
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: -6,
                    left: -6,
                    width: 12,
                    height: 12,
                    backgroundColor: '#1976d2',
                    border: '2px solid white',
                    borderRadius: '50%',
                    cursor: 'sw-resize',
                    pointerEvents: 'auto',
                    zIndex: 11
                  }}
                  onMouseDown={(e) => {
                    console.log('🔴 Resize handle clicked:', 'sw');
                    handleResizeStart(e, element, 'sw');
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: -6,
                    right: -6,
                    width: 12,
                    height: 12,
                    backgroundColor: '#1976d2',
                    border: '2px solid white',
                    borderRadius: '50%',
                    cursor: 'se-resize',
                    pointerEvents: 'auto',
                    zIndex: 11
                  }}
                  onMouseDown={(e) => {
                    console.log('🔴 Resize handle clicked:', 'se');
                    handleResizeStart(e, element, 'se');
                  }}
                />

                {/* Edge resize handles */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: -6,
                    width: 12,
                    height: 12,
                    backgroundColor: '#1976d2',
                    border: '2px solid white',
                    borderRadius: '50%',
                    cursor: 'w-resize',
                    pointerEvents: 'auto',
                    transform: 'translateY(-50%)',
                    zIndex: 11
                  }}
                  onMouseDown={(e) => {
                    console.log('🔴 Resize handle clicked:', 'w');
                    handleResizeStart(e, element, 'w');
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    right: -6,
                    width: 12,
                    height: 12,
                    backgroundColor: '#1976d2',
                    border: '2px solid white',
                    borderRadius: '50%',
                    cursor: 'e-resize',
                    pointerEvents: 'auto',
                    transform: 'translateY(-50%)',
                    zIndex: 11
                  }}
                  onMouseDown={(e) => {
                    console.log('🔴 Resize handle clicked:', 'e');
                    handleResizeStart(e, element, 'e');
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    top: -6,
                    left: '50%',
                    width: 12,
                    height: 12,
                    backgroundColor: '#1976d2',
                    border: '2px solid white',
                    borderRadius: '50%',
                    cursor: 'n-resize',
                    pointerEvents: 'auto',
                    transform: 'translateX(-50%)',
                    zIndex: 11
                  }}
                  onMouseDown={(e) => {
                    console.log('🔴 Resize handle clicked:', 'n');
                    handleResizeStart(e, element, 'n');
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: -6,
                    left: '50%',
                    width: 12,
                    height: 12,
                    backgroundColor: '#1976d2',
                    border: '2px solid white',
                    borderRadius: '50%',
                    cursor: 's-resize',
                    pointerEvents: 'auto',
                    transform: 'translateX(-50%)',
                    zIndex: 11
                  }}
                  onMouseDown={(e) => {
                    console.log('🔴 Resize handle clicked:', 's');
                    handleResizeStart(e, element, 's');
                  }}
                />
              </Box>
            </Box>
          ) : (
            <Box sx={{
              width: '100px',
              height: '100px',
              backgroundColor: '#f0f0f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 1
            }}>
              <Typography variant="caption" color="text.secondary">
                📷 Click to add image
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );

case ELEMENT_TYPES.CROPPABLE_IMAGE:
  return (
    <ImageCropper
      element={element}
      updateElement={updateElement}
      ElementControls={ElementControls}
      isInZone={isInZone}
      layoutId={layoutId}
      zoneId={zoneId}
      onElementClick={(element, isInZone, layoutId, zoneId) => {
        console.log('🔴 Croppable Image clicked - selecting element:', element.id);
        setSelectedElement(element);
        setSelectedElementContext({ isInZone, layoutId, zoneId });
        setTabValue(0); // Set to Content tab for editing image
        setRightPanelOpen(true); // Ensure right panel is open
      }}
    />
  );


// ...inside your render function:
case ELEMENT_TYPES.CIRCULAR_IMAGE:
  return (
    <CircularImageCropper
      element={element}
      updateElement={updateElement}
      ElementControls={ElementControls}
      isInZone={isInZone}
      layoutId={layoutId}
      zoneId={zoneId}
      onElementClick={(element, isInZone, layoutId, zoneId) => {
        console.log('🔴 Circular Image clicked - selecting element:', element.id);
        setSelectedElement(element);
        setSelectedElementContext({ isInZone, layoutId, zoneId });
        setTabValue(0); // Set to Content tab for editing image
        setRightPanelOpen(true); // Ensure right panel is open
      }}
    />
  );

case ELEMENT_TYPES.FITTABLE_IMAGE:
  return (
    <Box
      data-element-id={element.id}
      sx={{
        position: 'relative',
        '&:hover .element-controls': { opacity: 1 },
        mt: 0,
        mb: 0,
        // Don't apply selection styles to avoid double borders
        // The FittableImage component handles its own visual feedback
      }}
      onClick={e => {
        e.stopPropagation();
        setSelectedElement(element);
        setSelectedElementContext({ isInZone, layoutId, zoneId });
        setTabValue(0); // Content tab for editing image
        setRightPanelOpen(true);
      }}
      title="Click to select and edit fittable image"
    >
      <ElementControls />
      <FittableImage
        element={element}
        updateElement={updateElement}
        ElementControls={ElementControls}
        isInZone={isInZone}
        layoutId={layoutId}
        zoneId={zoneId}
        onElementClick={(element, isInZone, layoutId, zoneId) => {
          console.log('🔴 Fittable Image clicked - selecting element:', element.id);
          setSelectedElement(element);
          setSelectedElementContext({ isInZone, layoutId, zoneId });
          setTabValue(0); // Set to Content tab for editing image
          setRightPanelOpen(true); // Ensure right panel is open
        }}
      />
    </Box>
  );

case ELEMENT_TYPES.IMAGE_OVERLAY:
  const containerWidth = typeof element.styles?.width === 'number' ? `${element.styles.width}px` : (element.styles?.width || '100%');
  const containerHeight = typeof element.styles?.height === 'number' ? `${element.styles.height}px` : (element.styles?.height || 'auto');

  return (
    <Box
      data-element-id={element.id}
      sx={{
        position: 'relative',
        width: containerWidth,
        height: containerHeight,
        marginBottom: element.styles?.marginBottom || '10px',
        borderRadius: element.styles?.borderRadius || '8px',
        '&:hover .element-controls': { opacity: 1 },
        ...getSelectionStyles(element.id),
        ...element.styles,
        overflow: 'visible' // To show resize handles
      }}
    >
      <ElementControls />
      {/* The Background Image (Fittable) */}
      <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
        <FittableImage
          element={element}
          updateElement={updateElement}
          ElementControls={ElementControls}
          isInZone={isInZone}
          layoutId={layoutId}
          zoneId={zoneId}
          onElementClick={(element, isInZone, layoutId, zoneId) => {
            console.log('🔴 Image Overlay clicked - selecting element:', element.id);
            setSelectedElement(element);
            setSelectedElementContext({ isInZone, layoutId, zoneId });
            setTabValue(0); // Content tab for image settings
            setRightPanelOpen(true);
          }}
        />
      </Box>

      {/* The Overlay Box */}
      <Box
        sx={{
          position: 'absolute',
          zIndex: element.overlayStyles?.zIndex || 10,
          pointerEvents: 'auto',
          ...element.overlayStyles
        }}
        onClick={(e) => {
          e.stopPropagation();
          console.log('🔴 Overlay Box clicked - selecting element:', element.id);
          setSelectedElement(element);
          setSelectedElementContext({ isInZone, layoutId, zoneId });
          setTabValue(0); // Content tab for image settings
          setRightPanelOpen(true);
        }}
      >
        <Box
          onDragOver={(e) => handleDragOver(e, { layoutId: element.id, zoneId: 'overlay-zone' })}
          onDragLeave={handleDragLeave}
          onDrop={(e) => handleDrop(e, element.id, 'overlay-zone')}
          sx={{
            minHeight: '60px',
            border: targetZone?.layoutId === element.id && targetZone?.zoneId === 'overlay-zone' ? '2px dashed #1976d2' : 'none',
            borderRadius: '4px',
            backgroundColor: targetZone?.layoutId === element.id && targetZone?.zoneId === 'overlay-zone' ? 'rgba(25, 118, 210, 0.05)' : 'transparent',
          }}
        >
          {element.zones[0].elements.length === 0 ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', p: 2 }}>
              Drop elements here
            </Typography>
          ) : (
            element.zones[0].elements.map((zoneElement, elemIndex) => (
              <Box key={zoneElement.id} sx={{ mb: 1 }}>
                {renderInlineEditableElement(zoneElement, true, element.id, 'overlay-zone', elemIndex)}
              </Box>
            ))
          )}
        </Box>
      </Box>
    </Box>
  );
    default:
      return null;
  }
};
